import type { VoiceDraft } from './consoleVoiceDraft';
import type { SupabaseClient } from '../../lib/supabase';
import { decodeMessageReport } from '../tracker/messageReport';
import type { CurrentMessageReport } from '../tracker/messageReport';
import { readProspectingAvailability } from '../tracker/rolloutAvailability';
import { readAppEnv } from '../../lib/env';
import type { ConsoleGroup } from './consoleGroup';
import { prepareHuddleScreenshot } from './huddleImage';

export type HuddleStatus = 'queue' | 'in_progress' | 'done';

export interface HuddleComment {
  id: string;
  author: string;
  author_avatar_url: string | null;
  body: string;
  created_at: string;
}

export interface HuddleFile {
  id: string;
  name: string;
  mime_type: string | null;
  source: string;
}

export interface HuddleTask {
  id: string;
  title: string;
  status: HuddleStatus;
  due_date: string | null;
  /**
   * The TAP Console tab (tasks.console_group, null read as 'others').
   * Optional: a read_huddle from before the column omits it. Read it through
   * consoleGroupOf, never directly.
   */
  group?: ConsoleGroup;
  comments: HuddleComment[];
  files: HuddleFile[];
}

export interface HuddlePayload {
  section_name: string;
  owner_first_name: string;
  owner_avatar_url: string | null;
  tasks: HuddleTask[];
}

export interface ConsoleQuote {
  id: string;
  author: string | null;
  excerpt: string | null;
  available: boolean;
}
export interface ConsoleSearchResult {
  id: string;
  author: string;
  created_at: string;
  excerpt: string;
}
export interface ConsoleSearch {
  results: ConsoleSearchResult[];
  has_more: boolean;
  next_cursor: { id: string; created_at: string } | null;
  available: boolean;
}
export interface ConsoleMessage {
  quoted_comment_id?: string | null;
  quote?: ConsoleQuote | null;
  id: string;
  mine: boolean;
  author: string;
  author_avatar_url: string | null;
  body: string;
  created_at: string;
}
export interface ConsoleMessages {
  messages: ConsoleMessage[];
  unread: number;
  has_more: boolean;
}
export interface ConsoleAlert {
  kind: 'comment' | 'message';
  task_id: string | null;
  task_title: string | null;
  group: ConsoleGroup | null;
  status: string | null;
  is_done: boolean;
  comment_id: string;
  author: string;
  body: string;
  created_at: string;
}
export interface ConsoleAlerts {
  items: ConsoleAlert[];
  total: number;
  per_task: Record<string, number>;
  messages_unread: number;
}

export class HuddleError extends Error {
  constructor(public readonly kind: 'off' | 'temporary' | 'comment' | 'upload' | 'rate') {
    super(kind);
  }
}

export async function uploadHuddleScreenshot(
  client: SupabaseClient,
  token: string,
  taskId: string,
  file: File,
): Promise<string> {
  let upload = file;
  try {
    upload = await prepareHuddleScreenshot(file);
  } catch {
    // Compression is an optimization. The server still validates and accepts
    // the original supported image when a browser cannot prepare it locally.
  }
  const body = new FormData();
  body.set('token', token);
  body.set('taskId', taskId);
  body.set('file', upload);
  const { data, error } = (await client.functions.invoke('huddle-upload', {
    body,
  })) as { data: { url?: unknown } | null; error: unknown };
  if (error !== null || typeof data?.url !== 'string') throw new HuddleError('upload');
  return data.url;
}

export async function loadHuddle(client: SupabaseClient, token: string): Promise<HuddlePayload> {
  const { data, error } = await client.rpc('read_huddle', { p_token: token });
  if (error) throw new HuddleError('temporary');
  if (data === null) throw new HuddleError('off');
  return data as unknown as HuddlePayload;
}

export async function sendHuddleComment(
  client: SupabaseClient,
  token: string,
  taskId: string,
  body: string,
  ownerClient?: SupabaseClient,
): Promise<void> {
  // A teammate signed in to the Hub on this browser comments as themselves.
  // The database only accepts that for the owner of this link and returns
  // null for anyone else, so only a null answer falls through to the link
  // path. A real error must not: that would store the comment as Alex again.
  if (ownerClient) {
    const { data: session } = await ownerClient.auth.getSession();
    if (session.session) {
      const owned = await ownerClient.rpc('add_huddle_comment_owner', {
        p_token: token,
        p_task_id: taskId,
        p_body: body,
      });
      if (owned.error) throw new HuddleError('comment');
      if (owned.data !== null) return;
    }
  }
  const { data, error } = await client.rpc('add_huddle_comment', {
    p_token: token,
    p_task_id: taskId,
    p_body: body,
  });
  if (error || data === null) throw new HuddleError('comment');
}

export async function loadConsoleMessages(
  client: SupabaseClient,
  token: string,
  opts: { beforeAt?: string; beforeId?: string; limit?: number } = {},
): Promise<ConsoleMessages> {
  const { data, error } = await client.rpc('read_console_messages', {
    p_token: token,
    // exactOptionalPropertyTypes: a missing cursor is left out, not sent as
    // null, so the SQL default (null, the newest page) applies.
    ...(opts.beforeAt === undefined ? {} : { p_before_at: opts.beforeAt }),
    ...(opts.beforeId === undefined ? {} : { p_before_id: opts.beforeId }),
    p_limit: opts.limit ?? 50,
  });
  if (!error && data === null) throw new HuddleError('off');
  if (
    error ||
    data === null ||
    typeof data !== 'object' ||
    !Array.isArray((data as Record<string, unknown>).messages) ||
    typeof (data as Record<string, unknown>).unread !== 'number' ||
    typeof (data as Record<string, unknown>).has_more !== 'boolean'
  )
    throw new HuddleError('temporary');
  const value = data as Record<string, unknown>;
  return {
    messages: parseConsoleRows(value.messages),
    unread: value.unread as number,
    has_more: value.has_more as boolean,
  };
}
export async function sendConsoleMessage(
  client: SupabaseClient,
  token: string,
  body: string,
  source: string | null = null,
): Promise<ConsoleMessage> {
  const { data, error } = await (source === null
    ? client.rpc('send_console_message', { p_token: token, p_body: body })
    : client.rpc('send_console_quote_reply', { p_token: token, p_body: body, p_source: source }));
  if (error?.message.includes('huddle_comment_rate_limited')) throw new HuddleError('rate');
  if (error || data === null || typeof data !== 'object' || Array.isArray(data))
    throw new HuddleError('comment');
  const value = data as Record<string, unknown>;
  return {
    quoted_comment_id: typeof value.quoted_comment_id === 'string' ? value.quoted_comment_id : null,
    quote: parseConsoleQuote(value.quote),
    id: String(value.id),
    mine: Boolean(value.mine),
    author: String(value.author),
    author_avatar_url: typeof value.author_avatar_url === 'string' ? value.author_avatar_url : null,
    body: String(value.body),
    created_at: String(value.created_at),
  };
}
export async function markConsoleMessagesRead(
  client: SupabaseClient,
  token: string,
): Promise<void> {
  const { error } = await client.rpc('mark_console_messages_read', { p_token: token });
  if (error) throw new HuddleError('temporary');
}
export async function loadConsoleAlerts(
  client: SupabaseClient,
  token: string,
): Promise<ConsoleAlerts> {
  const { data, error } = await client.rpc('read_console_alerts', { p_token: token });
  if (
    error ||
    data === null ||
    typeof data !== 'object' ||
    Array.isArray(data) ||
    !Array.isArray((data as Record<string, unknown>).items) ||
    typeof (data as Record<string, unknown>).total !== 'number' ||
    typeof (data as Record<string, unknown>).per_task !== 'object' ||
    (data as Record<string, unknown>).per_task === null ||
    typeof (data as Record<string, unknown>).messages_unread !== 'number'
  )
    throw new HuddleError('temporary');
  const value = data as Record<string, unknown>;
  return {
    items: value.items as ConsoleAlert[],
    total: value.total as number,
    per_task: value.per_task as Record<string, number>,
    messages_unread: value.messages_unread as number,
  };
}
export async function markConsoleTaskRead(
  client: SupabaseClient,
  token: string,
  taskId: string,
): Promise<void> {
  const { error } = await client.rpc('mark_console_task_read', {
    p_token: token,
    p_task_id: taskId,
  });
  if (error) throw new HuddleError('temporary');
}
export async function markConsoleAlertsRead(client: SupabaseClient, token: string): Promise<void> {
  const { error } = await client.rpc('mark_console_alerts_read', { p_token: token });
  if (error) throw new HuddleError('temporary');
}

export function huddleFileUrl(token: string, fileId: string): string {
  const root = readAppEnv().supabaseUrl.replace(/\/$/, '');
  return `${root}/functions/v1/huddle-file?token=${encodeURIComponent(token)}&file=${encodeURIComponent(fileId)}`;
}

export const LINKEDIN_TIER_COUNTS = [
  'tagged',
  'not_messaged',
  'requested',
  'accepted',
  'dm1',
  'dm2',
  'dm3',
  'messaged',
  'replied',
  'info_replies',
  'overviews_sent',
  'promoted',
  'calls_booked',
  'calls_held',
  'no_shows',
  'proposals_sent',
  'deals',
  'deals_won',
] as const;
export const LINKEDIN_WEEK_COUNTS = [
  'dms_sent',
  'replies',
  'info_replies',
  'overviews_sent',
  'promoted_to_lead',
  'calls_booked',
  'calls_held',
  'no_shows',
  'proposals_sent',
  'deals_created',
  'deals_won',
] as const;
export type LinkedInTier = 'tier1' | 'tier2';
export type LinkedInTierRow = { tier: LinkedInTier } & Record<
  (typeof LINKEDIN_TIER_COUNTS)[number],
  number
>;
export type LinkedInWeek = { tier: LinkedInTier; week_start: string } & Record<
  (typeof LINKEDIN_WEEK_COUNTS)[number],
  number
>;
export interface LinkedInMessage {
  tier: LinkedInTier;
  copy_set_name: string;
  archived: boolean;
  people_messaged: number;
  replied: number;
  info_replied: number;
  calls_booked: number;
  draft: boolean;
  one_off: boolean;
  message_version: string | null;
}
export interface LinkedInFunnel {
  generated_at: string;
  tiers: LinkedInTierRow[];
  weekly: LinkedInWeek[];
  by_message: LinkedInMessage[];
  message_report: CurrentMessageReport | null;
}
function funnelObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function funnelTier(value: unknown): value is LinkedInTier {
  return value === 'tier1' || value === 'tier2';
}
function funnelDate(value: unknown): value is string {
  return typeof value === 'string' && !Number.isNaN(Date.parse(value));
}
function funnelCounts(row: Record<string, unknown>, keys: readonly string[]): boolean {
  return keys.every(
    (key) => typeof row[key] === 'number' && Number.isSafeInteger(row[key]) && row[key] >= 0,
  );
}
export async function loadLinkedInFunnel(
  client: SupabaseClient,
  token: string,
): Promise<LinkedInFunnel> {
  const availability = await readProspectingAvailability(client, token);
  let data: unknown;
  try {
    const result = await client.rpc(
      availability.reader_enabled ? 'read_linkedin_funnel_message_used' : 'read_linkedin_funnel',
      { p_token: token },
    );
    if (result.error) throw new HuddleError('temporary');
    data = result.data;
  } catch {
    throw new HuddleError('temporary');
  }
  if (data === null) throw new HuddleError('off');
  if (
    !funnelObject(data) ||
    !funnelDate(data.generated_at) ||
    !Array.isArray(data.tiers) ||
    !Array.isArray(data.weekly) ||
    !Array.isArray(data.by_message) ||
    !data.tiers.every(
      (row) => funnelObject(row) && funnelTier(row.tier) && funnelCounts(row, LINKEDIN_TIER_COUNTS),
    ) ||
    !data.weekly.every(
      (row) =>
        funnelObject(row) &&
        funnelTier(row.tier) &&
        funnelDate(row.week_start) &&
        funnelCounts(row, LINKEDIN_WEEK_COUNTS),
    ) ||
    !data.by_message.every(
      (row) =>
        funnelObject(row) &&
        funnelTier(row.tier) &&
        typeof row.copy_set_name === 'string' &&
        typeof row.archived === 'boolean' &&
        funnelCounts(row, ['people_messaged', 'replied', 'info_replied', 'calls_booked']),
    )
  ) {
    throw new HuddleError('temporary');
  }
  // Project only the public contract; unrecognized response keys stay out.
  let messageReport: CurrentMessageReport | null = null;
  try {
    if (availability.reader_enabled)
      messageReport = decodeMessageReport({
        ...(funnelObject(data.message_report) ? data.message_report : {}),
        messages: data.by_message,
      });
  } catch {
    throw new HuddleError('temporary');
  }
  const counts = <K extends string>(row: Record<string, unknown>, keys: readonly K[]) =>
    Object.fromEntries(keys.map((key) => [key, row[key]])) as Record<K, number>;
  return {
    generated_at: data.generated_at,
    tiers: data.tiers.map((row: Record<string, unknown>) => ({
      tier: row.tier as LinkedInTier,
      ...counts(row, LINKEDIN_TIER_COUNTS),
    })),
    weekly: data.weekly.map((row: Record<string, unknown>) => ({
      tier: row.tier as LinkedInTier,
      week_start: row.week_start as string,
      ...counts(row, LINKEDIN_WEEK_COUNTS),
    })),
    by_message: data.by_message.map((row: Record<string, unknown>) => ({
      tier: row.tier as LinkedInTier,
      copy_set_name: row.copy_set_name as string,
      archived: row.archived as boolean,
      draft: row.draft as boolean,
      one_off: row.one_off as boolean,
      message_version: row.message_version as string | null,
      ...counts(row, ['people_messaged', 'replied', 'info_replied', 'calls_booked'] as const),
    })),
    message_report: messageReport,
  };
}

function parseConsoleQuote(raw: unknown): ConsoleQuote | null {
  if (raw == null) return null;
  const q = raw as Partial<ConsoleQuote>;
  if (
    typeof q.id !== 'string' ||
    typeof q.available !== 'boolean' ||
    (q.available && (typeof q.author !== 'string' || typeof q.excerpt !== 'string'))
  )
    throw new HuddleError('temporary');
  return {
    id: q.id,
    available: q.available,
    author: q.available ? q.author! : null,
    excerpt: q.available ? q.excerpt! : null,
  };
}
function parseConsoleRows(raw: unknown): ConsoleMessage[] {
  if (!Array.isArray(raw)) throw new HuddleError('temporary');
  return raw.map((row: ConsoleMessage) => {
    if (
      typeof row.id !== 'string' ||
      typeof row.body !== 'string' ||
      typeof row.author !== 'string' ||
      typeof row.mine !== 'boolean' ||
      !Number.isFinite(Date.parse(row.created_at)) ||
      (row.quoted_comment_id != null && typeof row.quoted_comment_id !== 'string')
    )
      throw new HuddleError('temporary');
    return { ...row, quote: parseConsoleQuote(row.quote) };
  });
}
export async function loadConsoleOwnerMessages(
  client: SupabaseClient,
  cursor?: { id: string; created_at: string },
): Promise<ConsoleMessages> {
  const { data, error } = await client.rpc('read_console_messages_owner', {
    p_limit: 25,
    ...(cursor ? { p_before_at: cursor.created_at, p_before_id: cursor.id } : {}),
  });
  if (error) throw new HuddleError('temporary');
  if (data === null) throw new HuddleError('off');
  const value = data as unknown as ConsoleMessages;
  if (typeof value.unread !== 'number' || typeof value.has_more !== 'boolean')
    throw new HuddleError('temporary');
  return { ...value, messages: parseConsoleRows(value.messages) };
}
export async function searchConsoleMessages(
  client: SupabaseClient,
  query: string,
  token: string | null,
  cursor?: { id: string; created_at: string },
): Promise<ConsoleSearch> {
  const { data, error } = await client.rpc('search_console_messages', {
    p_query: query,
    ...(token === null ? {} : { p_token: token }),
    p_limit: 25,
    ...(cursor ? { p_before_at: cursor.created_at, p_before_id: cursor.id } : {}),
  });
  if (error) throw new HuddleError('temporary');
  if (data === null) throw new HuddleError('off');
  const value = data as unknown as ConsoleSearch;
  if (
    !Array.isArray(value.results) ||
    typeof value.has_more !== 'boolean' ||
    typeof value.available !== 'boolean' ||
    value.results.some(
      (row) =>
        typeof row.id !== 'string' ||
        typeof row.author !== 'string' ||
        typeof row.excerpt !== 'string' ||
        !Number.isFinite(Date.parse(row.created_at)),
    ) ||
    (value.next_cursor !== null &&
      (typeof value.next_cursor?.id !== 'string' ||
        !Number.isFinite(Date.parse(value.next_cursor?.created_at)))) ||
    (value.has_more && value.next_cursor === null)
  )
    throw new HuddleError('temporary');
  return value;
}

export interface ConsoleVoiceMetadata {
  transcript: string;
  partial: boolean;
  duration: number;
  expires_at: string;
  audio_available: boolean;
}
export interface ConsoleVoiceAccess {
  metadata: (id: string) => Promise<ConsoleVoiceMetadata>;
  play: (id: string) => Promise<{ url: string }>;
}
export function consoleVoiceAccess(client: SupabaseClient, token?: string): ConsoleVoiceAccess {
  async function request(action: string, id: string): Promise<Record<string, unknown>> {
    const { data, error } = (await client.functions.invoke('console-voice', {
      body: { action, id, ...(token ? { token } : {}) },
    })) as { data: unknown; error: unknown };
    if (error || !data || typeof data !== 'object') throw Error('Voice note unavailable');
    return data as Record<string, unknown>;
  }
  return {
    metadata: async (id) => {
      const data = await request('metadata', id);
      if (
        typeof data.transcript !== 'string' ||
        typeof data.partial !== 'boolean' ||
        typeof data.duration !== 'number' ||
        typeof data.expires_at !== 'string' ||
        typeof data.audio_available !== 'boolean'
      )
        throw Error('Voice note unavailable');
      return data as unknown as ConsoleVoiceMetadata;
    },
    play: async (id) => {
      const data = await request('play', id);
      if (typeof data.url !== 'string') throw Error('Voice note unavailable');
      return { url: data.url };
    },
  };
}
export async function sendConsoleVoice(
  client: SupabaseClient,
  token: string,
  draft: VoiceDraft,
): Promise<string> {
  const body = new FormData();
  body.set('token', token);
  body.set('draft', draft.id);
  body.set('transcript', draft.transcript);
  body.set('partial', String(draft.partial));
  body.set('duration', String(draft.duration));
  body.set(
    'file',
    draft.blob,
    draft.blob.type.startsWith('audio/mp4') ? 'voice.m4a' : 'voice.webm',
  );
  const { data, error } = (await client.functions.invoke('console-voice', { body })) as {
    data: unknown;
    error: unknown;
  };
  const result = data as { id?: unknown } | null;
  if (error || typeof result?.id !== 'string') throw Error('Voice note did not send');
  return result.id;
}
